/**
 * Public API for client-side audit tracking.
 *
 * Usage (from the axios interceptor):
 *   track({ http_method: 'PUT', http_path: 'api/input-funds/abc', http_status: 200, duration_ms: 45 })
 *
 * Usage (from a React component via useTrackAction):
 *   track({ action: 'EXPORT', resource_type: 'report', resource_name: 'Q1 2026' })
 *
 * The function:
 *   1. Auto-classifies resource_type + action from http_method + http_path when not supplied.
 *   2. Extracts project_id / period_id from the current URL.
 *   3. Generates a deep link to the resource's canonical UI page.
 *   4. Enqueues the enriched event for batched delivery to POST /api/audit-logs/events.
 */
import { classify } from './classify';
import { deepLink } from './deepLink';
import { enqueue } from './queue';

/**
 * Extract project_id and period_id from the current window.location.
 * Covers all private route patterns defined in src/router/private/index.jsx.
 */
function _routeContext() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  // /project/quaters/:projectId[/:periodId/...]
  let m = path.match(/\/project\/quaters\/([^/]+)(?:\/([^/?]+))?/);
  if (m) return { project_id: m[1], period_id: m[2] || params.get('periodId') || null };

  // /project/:projectId/... (not 'quaters' or 'create')
  m = path.match(/\/project\/([^/?]+)/);
  if (m && m[1] !== 'quaters' && m[1] !== 'create') {
    return { project_id: m[1], period_id: params.get('periodId') || null };
  }

  // /data-extraction/:projectId/:periodId
  m = path.match(/\/data-extraction\/([^/]+)\/([^/?]+)/);
  if (m) return { project_id: m[1], period_id: m[2] };

  // /output-tables/:projectId/:periodId
  m = path.match(/\/output-tables\/([^/]+)\/([^/?]+)/);
  if (m) return { project_id: m[1], period_id: m[2] };

  // /report/fca/:reportingMemberState/:workflowId/:periodId  (and /esma/)
  m = path.match(/\/report\/(fca|esma)\/([^/]+)\/([^/]+)\/([^/?]+)/);
  if (m) return { project_id: m[3], period_id: m[4], report_type: m[1], member_state: m[2] };

  return { project_id: null, period_id: null, report_type: null, member_state: null };
}

/**
 * @param {object} partial  Any subset of the event fields. Missing fields are inferred.
 */
export function track(partial) {
  try {
    let { resource_type, action } = partial;

    // Auto-classify from HTTP method + path when either is absent
    if (partial.http_method && partial.http_path && (!resource_type || !action)) {
      const c = classify(partial.http_method, partial.http_path);
      resource_type = resource_type ?? c.resource_type;
      action = action ?? c.action;
    }

    // Nothing meaningful to log without an action
    if (!action) return;

    const { project_id, period_id, report_type, member_state } = {
      ..._routeContext(),
      // Caller-supplied context overrides URL-parsed values
      ...(partial.metadata?.context || {})
    };

    const page_url = window.location.pathname + window.location.search;

    enqueue({
      action,
      resource_type: resource_type ?? null,
      resource_id: partial.resource_id ?? null,
      resource_name: partial.resource_name ?? null,
      status: partial.status ?? 'SUCCEEDED',
      http_method: partial.http_method ?? null,
      http_path: partial.http_path ?? null,
      http_status: partial.http_status ?? null,
      duration_ms: partial.duration_ms ?? null,
      error_message: partial.error_message ?? null,
      metadata: {
        ...(partial.metadata || {}),
        context: {
          project_id: project_id || null,
          period_id: period_id || null,
          page_url,
          deep_link: deepLink(resource_type, {
            project_id,
            period_id,
            report_type,
            member_state,
            page_url
          }),
          // Preserve any caller-supplied context fields (they win over auto-detected ones)
          ...(partial.metadata?.context || {})
        }
      }
    });
  } catch (_) {
    // Audit tracking must never crash the application
  }
}
